import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { ArrowLeftOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import api from '@/services/api.service';
import assetGroupService, { type AssetGroupDto } from '@/services/asset-group.service';
import { useAuthStore } from '@/store/authStore';

const { Text, Title } = Typography;
const { TextArea } = Input;

interface CoaRow {
  id: string;
  account_code: string;
  account_name: string;
  is_group: boolean;
  account_type: string;
}

const GL_FIELDS: { name: keyof AssetGroupDto; label: string }[] = [
  { name: 'asset_cost_account_id', label: 'Asset cost' },
  { name: 'inventory_clearing_account_id', label: 'Inventory clearing' },
  { name: 'accumulated_depreciation_account_id', label: 'Accumulated depreciation' },
  { name: 'depreciation_expense_account_id', label: 'Depreciation expense' },
  { name: 'disposal_proceeds_account_id', label: 'Disposal proceeds' },
  { name: 'disposal_gain_account_id', label: 'Disposal gain' },
  { name: 'disposal_loss_account_id', label: 'Disposal loss' },
];

const AssetGroupsPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canView = Boolean(
    user?.is_super_admin ||
      user?.permissions?.includes('asset.groups.view') ||
      user?.permissions?.includes('asset.view')
  );
  const canEdit = Boolean(
    user?.is_super_admin ||
      user?.permissions?.includes('asset.groups.edit') ||
      user?.permissions?.includes('asset.edit')
  );

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<AssetGroupDto | null>(null);
  const [form] = Form.useForm();

  const { data: groups = [], isLoading } = useQuery({
    queryKey: ['asset-groups'],
    queryFn: () => assetGroupService.list(true),
    enabled: canView,
  });

  const { data: coa = [] } = useQuery({
    queryKey: ['coa', 'asset-groups'],
    queryFn: async () => {
      const res = await api.get('/finance/chart-of-accounts');
      const data = (res as { data?: unknown })?.data ?? res;
      return Array.isArray((data as { data?: CoaRow[] })?.data)
        ? (data as { data: CoaRow[] }).data
        : Array.isArray(data)
          ? (data as CoaRow[])
          : [];
    },
    enabled: canView,
  });
  const leafAccounts = useMemo(() => coa.filter((a) => !a.is_group), [coa]);
  const coaLabel = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of leafAccounts) {
      m.set(a.id, `${a.account_code} — ${a.account_name}`);
    }
    return m;
  }, [leafAccounts]);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      form.setFieldsValue(editing);
    } else {
      form.resetFields();
      form.setFieldsValue({ is_active: true });
    }
  }, [open, editing, form]);

  const saveMut = useMutation({
    mutationFn: async (body: Partial<AssetGroupDto>) => {
      if (editing) return assetGroupService.update(editing.id, body);
      return assetGroupService.create(body);
    },
    onSuccess: () => {
      message.success(editing ? 'Group updated' : 'Group created');
      setOpen(false);
      setEditing(null);
      void queryClient.invalidateQueries({ queryKey: ['asset-groups'] });
    },
    onError: (e: Error) => message.error(e.message || 'Save failed'),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => assetGroupService.remove(id),
    onSuccess: () => {
      message.success('Group deleted');
      void queryClient.invalidateQueries({ queryKey: ['asset-groups'] });
    },
    onError: (e: Error) => message.error(e.message || 'Delete failed'),
  });

  const cols: ColumnsType<AssetGroupDto> = [
    { title: 'Code', dataIndex: 'code', key: 'c', width: 120 },
    { title: 'Name', dataIndex: 'name', key: 'n', width: 180 },
    {
      title: 'Active',
      dataIndex: 'is_active',
      key: 'a',
      width: 90,
      render: (v: boolean) => (v ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>),
    },
    {
      title: 'Expense',
      dataIndex: 'depreciation_expense_account_id',
      key: 'e',
      ellipsis: true,
      render: (id: string | null) => (id ? coaLabel.get(id) || id.slice(0, 8) : '—'),
    },
    {
      title: 'Accum.',
      dataIndex: 'accumulated_depreciation_account_id',
      key: 'u',
      ellipsis: true,
      render: (id: string | null) => (id ? coaLabel.get(id) || id.slice(0, 8) : '—'),
    },
    {
      title: 'Cost',
      dataIndex: 'asset_cost_account_id',
      key: 'cost',
      ellipsis: true,
      render: (id: string | null) => (id ? coaLabel.get(id) || id.slice(0, 8) : '—'),
    },
    {
      title: '',
      key: 'x',
      width: 160,
      render: (_: unknown, r) =>
        canEdit ? (
          <Space>
            <Button
              type="link"
              size="small"
              onClick={() => {
                setEditing(r);
                setOpen(true);
              }}
            >
              Edit
            </Button>
            <Button type="link" danger size="small" onClick={() => delMut.mutate(r.id)}>
              Delete
            </Button>
          </Space>
        ) : null,
    },
  ];

  if (!canView) {
    return (
      <div style={{ padding: 24 }}>
        <Card>Access denied</Card>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: 8 }}>
      <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/asset/assets')}>
        Back to assets
      </Button>
      <Card style={{ marginTop: 8 }}>
        <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }} wrap>
          <div>
            <Title level={4} style={{ margin: 0 }}>
              Asset groups
            </Title>
            <Text type="secondary">
              Group-wise GL accounts. Depreciation / disposal / capitalize resolve group → company defaults → optional
              run override. Match assets via group selection (or free-text group code).
            </Text>
          </div>
          <Space>
            <Button onClick={() => navigate('/asset/accounting-defaults')}>Company defaults</Button>
            {canEdit && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditing(null);
                  setOpen(true);
                }}
              >
                New group
              </Button>
            )}
          </Space>
        </Space>
        <Table rowKey="id" loading={isLoading} dataSource={groups} columns={cols} pagination={false} scroll={{ x: 900 }} />
      </Card>

      <Modal
        title={editing ? `Edit group ${editing.code}` : 'New asset group'}
        open={open}
        onCancel={() => {
          setOpen(false);
          setEditing(null);
        }}
        destroyOnHidden
        footer={null}
        width={640}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(v) => {
            void saveMut.mutate({
              ...v,
              code: String(v.code || '').trim().toUpperCase(),
            });
          }}
        >
          <Form.Item name="code" label="Code" rules={[{ required: true }]}>
            <Input placeholder="e.g. VEHICLE" disabled={Boolean(editing)} />
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input placeholder="Vehicles" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
          {GL_FIELDS.map((f) => (
            <Form.Item key={f.name as string} name={f.name as string} label={f.label}>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                options={leafAccounts.map((a) => ({
                  value: a.id,
                  label: `${a.account_code} — ${a.account_name} (${a.account_type})`,
                }))}
              />
            </Form.Item>
          ))}
          <Button type="primary" htmlType="submit" loading={saveMut.isPending} block>
            Save
          </Button>
        </Form>
      </Modal>
    </div>
  );
};

export default AssetGroupsPage;
